import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FaBox, FaTags, FaTools, FaEnvelope, FaArrowRight, FaClock } from 'react-icons/fa';

const StatCard = ({ icon: Icon, label, value, color, to }) => (
  <Link to={to} className="bg-white rounded-xl shadow-sm p-6 flex items-center gap-4 hover:shadow-md transition-shadow group">
    <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
      <Icon className="text-white text-xl" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-gray-500 text-sm">{label}</p>
      <p className="text-2xl font-bold text-primary">{value ?? '—'}</p>
    </div>
    <FaArrowRight className="text-gray-300 group-hover:text-secondary transition-colors" />
  </Link>
);

export default function Dashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState({ products: 0, categories: 0, services: 0, messages: 0, unread: 0 });
  const [recentMessages, setRecentMessages] = useState([]);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get('/api/products/all', { headers }),
      axios.get('/api/categories/all', { headers }),
      axios.get('/api/services/all', { headers }),
      axios.get('/api/contact', { headers }),
    ]).then(([p, c, s, m]) => {
      const msgs = m.data;
      setStats({
        products: p.data.length,
        categories: c.data.length,
        services: s.data.length,
        messages: msgs.length,
        unread: msgs.filter(x => !x.is_read).length
      });
      setRecentMessages(msgs.slice(0, 5));
    }).catch(() => {});
  }, [token]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-primary">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Genel bakış ve son aktiviteler</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FaBox} label="Toplam Ürün" value={stats.products} color="bg-primary" to="/admin/urunler" />
        <StatCard icon={FaTags} label="Kategori" value={stats.categories} color="bg-secondary" to="/admin/kategoriler" />
        <StatCard icon={FaTools} label="Hizmet" value={stats.services} color="bg-primary-light" to="/admin/hizmetler" />
        <StatCard icon={FaEnvelope} label={`Mesaj (${stats.unread} okunmamış)`} value={stats.messages} color="bg-rose-500" to="/admin/mesajlar" />
      </div>

      {/* Recent Messages */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="font-semibold text-gray-700">Son Mesajlar</h3>
          <Link to="/admin/mesajlar" className="text-secondary text-sm hover:underline">Tümünü Gör</Link>
        </div>
        {recentMessages.length > 0 ? (
          <div className="divide-y">
            {recentMessages.map(msg => (
              <div key={msg.id} className={`px-6 py-4 flex items-start gap-3 ${!msg.is_read ? 'bg-blue-50/50' : ''}`}>
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!msg.is_read ? 'bg-secondary' : 'bg-gray-300'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="font-medium text-gray-700 text-sm truncate">{msg.name}</p>
                    <span className="text-xs text-gray-400 flex-shrink-0 flex items-center gap-1">
                      <FaClock size={10} />
                      {new Date(msg.created_at).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs truncate">{msg.subject || msg.message}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-10 text-center text-gray-400 text-sm">Henüz mesaj yok</div>
        )}
      </div>
    </div>
  );
}
